// resizeBase64Image.js
// Resize a Base64 image using an off-DOM canvas and return a new Base64 string.

/**
 * Resize a Base64 image and return a new Base64 string (data URL).
 * The canvas is created internally and never added to the DOM.
 *
 * @param {string} base64 - Source image as data URL (Base64).
 * @param {object} options - Resize options.
 * @param {number} [options.maxWidth=1200] - Max width of the resized image.
 * @param {number} [options.maxHeight=1200] - Max height of the resized image.
 * @param {string} [options.mimeType='image/jpeg'] - Output mime type (e.g. 'image/jpeg', 'image/png').
 * @param {number} [options.quality=0.8] - Output quality (for JPEG/WebP: 0–1).
 * @returns {Promise<string>} - Promise resolving to resized image as data URL.
 */
export function resizeBase64Image(
  base64,
  {
    maxWidth = 1200,
    maxHeight = 1200,
    mimeType = 'image/jpeg',
    quality = 0.8
  } = {}
) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Create an off-DOM canvas (not attached to the document)
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get 2D context from canvas.'));
        return;
      }

      // Calculate aspect-respecting scale
      const ratio = Math.min(
        maxWidth / img.width,
        maxHeight / img.height,
        1 // never upscale
      );

      const targetWidth = Math.round(img.width * ratio);
      const targetHeight = Math.round(img.height * ratio);

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.clearRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      try {
        const resizedBase64 = canvas.toDataURL(mimeType, quality);
        resolve(resizedBase64);
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load base64 image.'));
    };

    img.src = base64;
  });
}